'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import difference from 'hs-lodash/difference';
import { DragDropContext } from 'react-dnd-compat';
import IPEUIBackend from 'react-dnd-ipeui-backend';
import { Provider } from 'react-redux';
import { applyDiff } from 'editor-iframe-lib/redux/utils/iframeStateSync';
import { AppProvider } from 'foundations-components/components/AppProvider';
import Raven from 'raven-js';
import PickersContext from 'ContentComponents/context/PickersContext';
import { I18nContextProvider } from 'EmailEditorUI/context/I18nContext';
import { MODULE_PADDING_FLAG_KEY, MODULE_PADDING_CSS_KEY } from 'EmailEditorUI/utils/dragDropModuleConstants';
import { updateMultipleElementFields } from 'EmailEditorUI/redux/actions/LayoutActions';
import { getWebVersionElementIds } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import { getIsWebversionEnabled } from 'EmailEditorUI/WebVersion/selectors/webversionSelectors';
import EmailDragDropSyncConfigManager from 'EmailEditorUI/redux/inpageSync/EmailDragDropSyncConfigManager';
import { getParentCaresAboutNextIframeClick } from 'LayoutBuilderUI/selectors/appStatus';
import { getIsBlogRssModule } from 'EmailEditorUI/utils/moduleUtils';
import { getRssSaveData } from 'EmailEditorUI/redux/selectors/SaveSelectors';
import EmailKeyboardShortcutsContainer from 'EmailEditorUI/containers/EmailKeyboardShortcutsContainer';
import { renderModule } from 'EmailEditorUI/redux/actions/RenderActions';
import { FILE_UPLOAD_PROGRESS } from 'EmailEditorUI/redux/actions/LayoutBuilderActionTypes';
import { getAllModuleIdsInSections, getElements } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import ReducerDifferManager from 'ContentEditorUI/redux/ReducerDifferManager';
import { iframeClicked, renderModule as renderModuleInParent, closeHeaderMenu } from 'LayoutBuilderUI/actions/ApiActions';
import { DRAG_PREVIEW_HEIGHT_PX, SCROLL_AMOUNT } from 'LayoutBuilderUI/utils/DragDropUtils';
import StyleBreakpointsMarker from 'LayoutBuilderUI/components/utility/StyleBreakpointsMarker';
import { createUsageTrackerForSharedActions } from 'LayoutBuilderUI/utils/usageLoggingUtils';
import DarkReaderScript from 'LayoutBuilderUI/utils/DarkReaderScript';
import { pickerComponents } from 'LayoutBuilderUI/components/PickerComponents';
import PortaledSidebar from 'LayoutBuilderUI/components/PortaledSidebar';
import CommentableAreaCursorTooltip from 'LayoutBuilderUI/components/utility/CommentableAreaCursorTooltip';
import configureStore from 'LayoutBuilderUI/utils/configureStore';
import DraggingAwarenessDiv from 'LayoutBuilderUI/containers/DraggingAwarenessDiv';
import EditorDragLayer from 'LayoutBuilderUI/components/overlays/EditorDragLayer';
import LayoutContainer from 'LayoutBuilderUI/containers/LayoutContainer';
import OverrideableStyles from 'LayoutBuilderUI/components/utility/OverrideableStyles';
import PreviewFrameCopilotCollabSessionManager from 'LayoutBuilderUI/containers/PreviewFrameCopilotCollabSessionManager';
import PreviewFrameDynamicTextSessionManager from 'LayoutBuilderUI/AIDynamicText/PreviewFrameDynamicTextSessionManager';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class ApplicationDnd extends Component {
  constructor(props, context) {
    super(props, context);
    this.handleUploadFileProgress = message => {
      this.store.dispatch(Object.assign({
        type: FILE_UPLOAD_PROGRESS
      }, message));
    };
    this.updateLayoutScrollBy = newScroll => {
      const scrollContainer = this._scrollContainer;
      scrollContainer.scrollTop = scrollContainer.scrollTop + newScroll;
    };
    this.maybeUpdateScrollOnDrag = e => {
      const scrollContainer = this._scrollContainer;
      const dragLayerRef = this._dragLayer;
      const computedOffsets = dragLayerRef ? dragLayerRef.getComputedOffsetsOnDragItem() : {
        x: 0,
        y: 0
      };
      if (computedOffsets) {
        const dragItemTopAndBottom = {
          top: e.clientY + computedOffsets.y,
          bottom: e.clientY + computedOffsets.y + DRAG_PREVIEW_HEIGHT_PX
        };
        if (dragItemTopAndBottom.top - scrollContainer.clientTop <= 0) {
          this.updateLayoutScrollBy(-SCROLL_AMOUNT);
        } else if (dragItemTopAndBottom.bottom >= scrollContainer.clientHeight) {
          this.updateLayoutScrollBy(SCROLL_AMOUNT);
        }
      }
      return false;
    };
    this.onLayoutBuilderClicked = () => {
      closeHeaderMenu();
      if (getParentCaresAboutNextIframeClick(this.store.getState())) {
        iframeClicked();
      }
    };
    this.scrollContainerRef = ref => {
      this._scrollContainer = ref;
    };
    this.maybeFixupDeprecatedWebversionPadding = state => {
      if (!getIsWebversionEnabled(state)) {
        return;
      }
      const elements = getElements(state);
      const webversionIds = getWebVersionElementIds(state);
      const webversionId = webversionIds[0];
      const webversionWidget = elements.get(webversionId);
      // only modify when padding is applied
      if (!webversionWidget.getIn(['body', 'side_padding'])) {
        return;
      }
      const wrapperPaddingEnabled = webversionWidget.getIn(['body', MODULE_PADDING_FLAG_KEY]);
      const newPadding = wrapperPaddingEnabled ? {
        'padding-left': '40px',
        'padding-right': '40px',
        'padding-top': '10px',
        'padding-bottom': '10px'
      } : {
        'padding-left': '20px',
        'padding-right': '20px',
        'padding-top': '0px',
        'padding-bottom': '0px'
      };
      Raven.captureException(new Error('Cleaning up deprecated side padding in webversion'));
      this.store.dispatch(updateMultipleElementFields({
        id: webversionId,
        elementMultipleData: [{
          key: 'side_padding',
          value: false
        }, {
          key: MODULE_PADDING_CSS_KEY,
          value: newPadding
        }, {
          key: MODULE_PADDING_FLAG_KEY,
          value: true
        }]
      }));
    };
    if (!props.initialSyncState.contentReadOnlyData.isEmailTemplateEditor) {
      // It's possible that ReducerDifferManager has already been called before we
      // set the gates, which can lead to cached false negatives due to checking
      // against an empty list then caching the false values, so we explicitly clear
      // the cache first before setting the gates.
      ReducerDifferManager.clear();
      ReducerDifferManager.setGates(props.initialSyncState.auth.gates);
    }
    const initialHydratedSyncState = applyDiff(props.initialSyncState, {}, {
      config: EmailDragDropSyncConfigManager.getConfig()
    });
    this.store = configureStore(Object.assign({}, initialHydratedSyncState, {
      selected: Immutable.Map(),
      renderedModules: props.renderedModules
    }));
    this.themeName = props.initialSyncState.appStatus.isTrellisTheme ? 'trellis' : 'canvas';
    this.themeMode = 'light';
    props.bus.setStore(this.store);
    window.AppInstance = this;
    createUsageTrackerForSharedActions();
  }
  componentDidMount() {
    const {
      renderedModules,
      isNewEmail
    } = this.props;
    const allModulesInFlexArea = getAllModuleIdsInSections(this.store.getState());
    const unrenderedModuleIds = difference(allModulesInFlexArea, Object.keys(renderedModules));
    if (isNewEmail) {
      const elements = getElements(this.store.getState());
      elements.forEach((element, key) => {
        if (getIsBlogRssModule(element) && allModulesInFlexArea.includes(key)) {
          unrenderedModuleIds.push(key);
        }
      });
    }
    this.renderModules(unrenderedModuleIds);
    this.maybeFixupDeprecatedWebversionPadding(this.store.getState());
    this.props.bus.on('uploadFileProgress', this.handleUploadFileProgress);
  }
  renderModules(moduleIds) {
    const state = this.store.getState();
    const widgets = getElements(state);
    const modulesToRender = moduleIds.map(id => {
      const module = widgets.get(id);
      return module;
    });
    modulesToRender.forEach(module => {
      if (module) {
        if (getIsBlogRssModule(module)) {
          const contentModelId = state.contentModel.id; // Immer-only: always plain object
          const rssSaveData = getRssSaveData(state);
          this.store.dispatch(renderModule(module, false, contentModelId, Object.assign({}, rssSaveData, {
            flex_areas: {
              main: {
                boxFirstElementIndex: 0,
                boxLastElementIndex: 0,
                boxed: false,
                isSingleColumnFullWidth: false,
                sections: [{
                  columns: [{
                    id: 'builtin_column_0-0',
                    widgets: [module.get('name')],
                    width: 12
                  }],
                  id: 'builtin_section-0'
                }]
              }
            }
          }), false, renderModuleInParent));
        } else {
          this.store.dispatch(renderModule(module, false, null, null, false, renderModuleInParent));
        }
      }
    });
  }
  render() {
    const {
      I18nProvider
    } = this.props;
    return /*#__PURE__*/_jsx(AppProvider, {
      themeName: this.themeName,
      themeMode: this.themeMode,
      applyGlobalStyles: false,
      children: /*#__PURE__*/_jsx(Provider, {
        store: this.store,
        children: /*#__PURE__*/_jsxs(I18nContextProvider, {
          value: I18nProvider,
          children: [/*#__PURE__*/_jsxs(PickersContext.Provider, {
            value: pickerComponents,
            children: [/*#__PURE__*/_jsxs(DraggingAwarenessDiv, {
              id: "hs_cos_editor",
              onClick: this.onLayoutBuilderClicked,
              children: [/*#__PURE__*/_jsx(OverrideableStyles, {}), /*#__PURE__*/_jsx(StyleBreakpointsMarker, {}), /*#__PURE__*/_jsx(EditorDragLayer, {
                ref: ref => this._dragLayer = ref
              }), /*#__PURE__*/_jsx(PortaledSidebar, {}), /*#__PURE__*/_jsx(LayoutContainer, {
                maybeUpdateScrollOnDrag: this.maybeUpdateScrollOnDrag,
                scrollContainerRef: this.scrollContainerRef
              }), /*#__PURE__*/_jsx(EmailKeyboardShortcutsContainer, {}), /*#__PURE__*/_jsx(DarkReaderScript, {})]
            }), /*#__PURE__*/_jsx(PreviewFrameCopilotCollabSessionManager, {}), /*#__PURE__*/_jsx(PreviewFrameDynamicTextSessionManager, {})]
          }), /*#__PURE__*/_jsx(CommentableAreaCursorTooltip, {})]
        })
      })
    });
  }
}
ApplicationDnd.propTypes = {
  bus: PropTypes.object.isRequired,
  initialSyncState: PropTypes.object.isRequired,
  renderedModules: PropTypes.object.isRequired,
  isNewEmail: PropTypes.bool,
  I18nProvider: PropTypes.object.isRequired
};
export default DragDropContext(IPEUIBackend)(ApplicationDnd);